// Adding an employer the way the driver app does it: search the registry
// first, type only once it has answered. A carrier picked here carries its DOT
// number, and the DOT is what lets a verification request find a contact.

import { useEffect, useReducer, useRef, useState } from 'react'

import { Divider, Loader, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { Check, X } from 'lucide-react'
import { zodResolver } from 'mantine-form-zod-resolver'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnDrawer from 'src/components/Core/DnDrawer'
import { RECRUITER_SURFACE } from 'src/components/Inputs/CarrierAutocomplete/contactTelemetry'
import type { KnownContact } from 'src/components/Inputs/CarrierAutocomplete/knownContacts'
import {
  MIN_QUERY_LENGTH,
  useCarrierSearch,
  type FmcsaSearchCompany,
} from 'src/components/Inputs/CarrierAutocomplete/useCarrierSearch'
import { useCarrierSearchTelemetry } from 'src/components/Inputs/CarrierAutocomplete/useCarrierSearchTelemetry'
import {
  isBlankContact,
  useContactChoice,
} from 'src/components/Inputs/CarrierAutocomplete/useContactChoice'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_APPLICATION,
  GET_APPLICATION_BY_APPLICANT_ID,
} from 'src/graphql/application.graphql'
import {
  CREATE_EMPLOYMENT_EXPERIENCE,
  GET_EMPLOYMENT_EXPERIENCES_BY_APPLICATION_ID,
} from 'src/graphql/employmentExperiences.graphql'
import { EmploymentExperienceSchema } from 'src/validations'

import {
  addEmploymentFlow,
  fieldsAfterLeavingPick,
  initialFlow,
  replacesEmployer,
  stageForField,
  type EmployerIdentity,
  type Stage,
} from './addEmploymentFlow'
import ContactStage from './ContactStage'
import EmployerStage from './EmployerStage'
import {
  initialEmploymentValues,
  type EmploymentFormValues,
} from './formValues'
import JobStage from './JobStage'

const COMPONENT = 'AddEmploymentDrawer'

interface AddEmploymentDrawerProps {
  opened: boolean
  close: () => void
  applicationId: string
  applicantId?: string
  initialStartDate?: Date
  initialEndDate?: Date
}

const AddEmploymentDrawer = ({
  opened,
  close,
  applicationId,
  applicantId,
  initialStartDate,
  initialEndDate,
}: AddEmploymentDrawerProps) => {
  const form = useForm<EmploymentFormValues>({
    initialValues: initialEmploymentValues({
      initialStartDate,
      initialEndDate,
    }),
    validate: zodResolver(EmploymentExperienceSchema),
  })
  const [flow, dispatch] = useReducer(addEmploymentFlow, undefined, initialFlow)

  // Any section folds to its title; the stage the recruiter moves to opens itself.
  const [folded, setFolded] = useState<Record<Stage, boolean>>({
    employer: false,
    contact: false,
    job: false,
  })
  const toggleFold = (stage: Stage) =>
    setFolded((state) => ({ ...state, [stage]: !state[stage] }))
  useEffect(() => {
    setFolded((state) =>
      state[flow.stage] ? { ...state, [flow.stage]: false } : state
    )
  }, [flow.stage])

  const search = useCarrierSearch(
    flow.employerMode === 'search' ? flow.query : ''
  )
  const telemetry = useCarrierSearchTelemetry({
    component: COMPONENT,
    surface: RECRUITER_SURFACE,
  })

  const contactChoice = useContactChoice({
    component: COMPONENT,
    dotNumber: form.values.dotNumber,
    values: form.values,
    setContactFields: ({ contactName, phone, email }) => {
      form.setFieldValue('contactName', contactName)
      form.setFieldValue('phone', phone)
      form.setFieldValue('email', email)
    },
  })

  const [createEmploymentExperience, { loading: saving }] = useMutation(
    CREATE_EMPLOYMENT_EXPERIENCE,
    {
      refetchQueries: [
        {
          query: GET_EMPLOYMENT_EXPERIENCES_BY_APPLICATION_ID,
          variables: { applicationId },
        },
        { query: GET_APPLICATION, variables: { id: applicationId } },
        ...(applicantId
          ? [
              {
                query: GET_APPLICATION_BY_APPLICANT_ID,
                variables: { applicantId },
              },
            ]
          : []),
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('Employment experience created successfully', 'success')
      },
      onError: (error) => {
        toast('Failed to create employment experience', 'error', { error })
      },
    }
  )

  // One fmcsa_search_results per settled answer, keyed on what settled —
  // never on the keystrokes or the form, which is what over-fired before.
  const query = flow.query.trim()
  const settled =
    flow.employerMode === 'search' &&
    query.length >= MIN_QUERY_LENGTH &&
    search.searched &&
    !search.searching
  const outcomeKey = settled
    ? `${query}:${search.results.length}:${search.failed}:${search.throttled}`
    : null
  const reportedOutcome = useRef<string | null>(null)
  useEffect(() => {
    if (!outcomeKey || reportedOutcome.current === outcomeKey) return
    reportedOutcome.current = outcomeKey
    telemetry.searchResults({
      query,
      resultsCount: search.results.length,
      normalizedQuery: search.normalizedQuery,
      dotLookup: search.dotLookup,
    })
  }, [outcomeKey, query, search, telemetry])

  // The drawer unmounts on every close, so a search left mid-way is reported
  // exactly once, there.
  const telemetryRef = useRef(telemetry)
  telemetryRef.current = telemetry
  useEffect(() => () => telemetryRef.current.searchAbandoned(), [])

  // The employer the recruiter is replacing. Kept here rather than in the form
  // so that Change leaves nothing behind for a Save to store.
  const lastEmployer = useRef<EmployerIdentity | null>(null)
  const currentEmployer = (): EmployerIdentity | null =>
    form.values.companyName
      ? { name: form.values.companyName, dotNumber: form.values.dotNumber }
      : lastEmployer.current

  const blankContact = () => {
    form.setValues({ contactName: '', email: '', phone: '' })
    contactChoice.reset()
  }

  const applyCarrier = (company: FmcsaSearchCompany) => {
    const next = { name: company.companyName, dotNumber: company.dotNumber }
    const replacing = replacesEmployer(currentEmployer(), next)
    lastEmployer.current = next
    form.setValues({
      companyName: company.companyName ?? '',
      dotNumber: company.dotNumber ?? '',
      address: {
        street: company.address?.street ?? '',
        city: company.address?.city ?? '',
        state: company.address?.state ?? '',
        zipCode: company.address?.zipCode ?? '',
        country: company.address?.country ?? '',
      },
    })
    if (replacing) blankContact()
    dispatch({
      type: 'pickCarrier',
      dotNumber: company.dotNumber ?? null,
      offerList: replacing || isBlankContact(form.values),
    })
  }

  const handlePick = (company: FmcsaSearchCompany, rank: number) => {
    telemetry.carrierSelected(
      { rank, dotNumber: company.dotNumber },
      {
        search_term: query,
        results_count: search.results.length,
        search_type: search.dotLookup ? 'dot_number' : 'company_name',
        in_directory: company.inDirectory ?? null,
      }
    )
    applyCarrier(company)
  }

  // DotAutocomplete reports this pick itself; a second event would double-count it
  const handleDotLookupPick = (company: FmcsaSearchCompany) => {
    telemetry.resolved()
    applyCarrier(company)
  }

  const handleAddAsTyped = ({
    registryAnswered,
  }: {
    registryAnswered: boolean
  }) => {
    telemetry.manualEntry(
      {
        query,
        resultsCount: search.results.length,
        normalizedQuery: search.normalizedQuery,
        dotLookup: search.dotLookup,
        searchedBeforeManual: registryAnswered,
      },
      { non_driving_job: false, registry_timed_out: !registryAnswered }
    )
    // A typed employer keeps whatever DOT and address were typed with it;
    // only a registry pick's facts are cleared, and Change already did that.
    const next = { name: query, dotNumber: form.values.dotNumber }
    const replacing = replacesEmployer(currentEmployer(), next)
    lastEmployer.current = next
    form.setValues({ companyName: query })
    if (replacing) blankContact()
    dispatch({ type: 'addAsTyped' })
  }

  // A typed employer reopens for editing: what the recruiter entered is theirs.
  // A pick goes back to the search: its DOT, address and fax belonged to the
  // registry, and the name is remembered only to tell the next employer apart.
  const handleChangeEmployer = () => {
    if (flow.employerMode === 'typed') {
      dispatch({ type: 'showStage', stage: 'employer' })
      return
    }
    lastEmployer.current = currentEmployer()
    // Searching again is a new interaction, reported afresh
    reportedOutcome.current = null
    form.setValues({ companyName: '', ...fieldsAfterLeavingPick() })
    form.clearFieldError('companyName')
    dispatch({ type: 'changeEmployer' })
  }

  const handleContinueEmployer = () => {
    const errors = [
      form.validateField('companyName'),
      form.validateField('dotNumber'),
    ]
    if (errors.some((result) => result.hasError)) return
    dispatch({
      type: 'continueEmployer',
      offerList: isBlankContact(form.values),
    })
  }

  const handlePickContact = (contact: KnownContact) => {
    contactChoice.selectKnownContact(contact)
    dispatch({ type: 'pickContact' })
  }

  const handleDifferentContact = () => {
    blankContact()
    dispatch({ type: 'differentContact' })
  }

  const handleContinueContact = () => {
    const errors = [form.validateField('email'), form.validateField('phone')]
    if (errors.some((result) => result.hasError)) return
    dispatch({ type: 'continueContact' })
  }

  const buildInput = () => {
    const values = form.values
    const address = {
      street: values.address?.street?.trim() || '',
      city: values.address?.city?.trim() || '',
      state: values.address?.state?.trim() || '',
      zipCode: values.address?.zipCode?.trim() || '',
      country: values.address?.country?.trim() || '',
    }
    const hasAddress = Object.values(address).some(Boolean)

    return {
      companyName: values.companyName?.trim(),
      position: values.position?.trim(),
      startDate: values.startDate,
      endDate: values.endDate,
      reasonForLeaving: values.reasonForLeaving?.trim(),
      dotNumber: values.dotNumber?.trim() || null,
      contactName: values.contactName?.trim() || null,
      email: values.email?.trim() || null,
      phone: values.phone?.trim() || null,
      faxNumber: values.faxNumber?.trim() || null,
      unemploymentReason: values.unemploymentReason?.trim() || null,
      equipmentTypes: values.equipmentTypes || [],
      isDriverTerminated: values.isDriverTerminated,
      isDriverSubjectToFMCSRs: values.isDriverSubjectToFMCSRs,
      isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
        values.isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
      shouldContactEmployer: values.shouldContactEmployer,
      isUnemployment: false,
      ...(hasAddress && { address }),
    }
  }

  const handleSubmit = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      const [firstField] = Object.keys(validation.errors)
      if (firstField) {
        const stage = stageForField(firstField)
        setFolded((state) => ({ ...state, [stage]: false }))
        dispatch({ type: 'showStage', stage })
      }
      return
    }

    const input = buildInput()
    // Decided now, while the form holds what is being saved; sent on success.
    // A new entry has nothing stored, so any typed contact counts as manual
    const emitContactTelemetry = contactChoice.prepareSaveTelemetry(null)

    try {
      const result = await createEmploymentExperience({
        variables: { applicationId, input },
      })
      if (!result?.data?.createEmploymentExperience) return
      emitContactTelemetry()
      contactChoice.reset()
      close()
    } catch (error) {
      // Reported by the mutation's onError
    }
  }

  const handleCancel = () => {
    form.reset()
    contactChoice.reset()
    close()
  }

  const showList =
    flow.contactList &&
    (contactChoice.knownContactsLoading ||
      contactChoice.knownContacts.length > 0)

  return (
    <DnDrawer opened={opened} onClose={close}>
      <div className="flex h-screen flex-col space-y-4 pb-14">
        <div className="flex flex-row items-center justify-between">
          <Text size="lg" fw={500}>
            Add Employment Experience
          </Text>
          <div className="flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={handleSubmit}
              disabled={saving || !flow.reachedJob}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={handleCancel}
              disabled={saving}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
            {saving && <Loader size={12} color="gray" />}
          </div>
        </div>
        <Divider variant="dashed" />

        <div className="flex h-screen flex-col space-y-4 overflow-auto">
          <EmployerStage
            mode={flow.employerMode}
            collapsed={flow.stage !== 'employer'}
            form={form}
            query={flow.query}
            queryError={form.errors.companyName}
            onQueryChange={(value) => dispatch({ type: 'query', query: value })}
            search={search}
            onPick={handlePick}
            onAddAsTyped={handleAddAsTyped}
            onDotLookupPick={handleDotLookupPick}
            onChange={handleChangeEmployer}
            onSearchInstead={() => dispatch({ type: 'searchInstead' })}
            onContinue={handleContinueEmployer}
            folded={folded.employer}
            onToggleFold={() => toggleFold('employer')}
          />

          {flow.stage !== 'employer' && (
            <>
              <Divider variant="dashed" />
              <ContactStage
                collapsed={flow.stage === 'job'}
                form={form}
                contacts={contactChoice.knownContacts}
                loading={contactChoice.knownContactsLoading}
                showList={showList}
                onPickContact={handlePickContact}
                onDifferentContact={handleDifferentContact}
                onShowList={() => dispatch({ type: 'showContactList' })}
                onContinue={handleContinueContact}
                onChange={() => dispatch({ type: 'changeContact' })}
                folded={folded.contact}
                onToggleFold={() => toggleFold('contact')}
              />
            </>
          )}

          {flow.stage === 'job' && (
            <>
              <Divider variant="dashed" />
              <JobStage
                form={form}
                saving={saving}
                onSave={handleSubmit}
                folded={folded.job}
                onToggleFold={() => toggleFold('job')}
              />
            </>
          )}
        </div>
      </div>
    </DnDrawer>
  )
}

export default AddEmploymentDrawer
